import { useState } from 'react'
import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { Textarea } from '../ui/textarea'
import { Sparkles, FileText, BookImage } from 'lucide-react'
import { cn } from '@/lib/utils'

interface DescriptionCoverPromptDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: (descriptionPromptTemplate: string, coverPromptTemplate: string, options?: { coverModel?: string }) => void
  title: string
  author: string
}

// Description prompt templates with {title} and {author} placeholders
interface DescriptionPromptTemplate {
  id: string
  name: string
  description: string
  template: string
}

const DESCRIPTION_PROMPT_TEMPLATES: DescriptionPromptTemplate[] = [
  {
    id: 'default',
    name: 'Default Blurb',
    description: 'Compelling book blurb with hook, conflict, and cliffhanger',
    template: `Generate a Book blurb for book "{title}" by {author} that will be added to my book store. up to 100 words.

Start with the hook: One strong sentence that tells the central conflict, question, or promise. It must make the reader curious. Describe the central tension without spoilers. Match the tone to the genre. Keep paragraphs short (2–4 lines). Use active, simple language. End with a cliffhanger or promise.

Check internet if needed.`
  }
]

// Cover prompt templates with {title}, {author}, and {description} placeholders
interface CoverPromptTemplate {
  id: string
  name: string
  description: string
  template: string
  model?: string
}

const COVER_PROMPT_TEMPLATES: CoverPromptTemplate[] = [
  {
    id: 'nano-banana-3-pro',
    name: 'Nano Banana 3 Pro',
    description: 'Realistic cover with genre awareness (uses Gemini 3 Pro)',
    model: 'gemini-3-pro-image-preview',
    template: `Generate a realistic ebook cover for:

Book Title: {title}
Author: {author}

The cover should include the title "{title}" and author name "{author}" prominently displayed.
Create a book cover that clearly communicates the genre and appeals to the target audience, using a strong visual concept, appropriate mood, and readable typography. 
Include the title, and author name with clear hierarchy, and design the layout so it remains attractive and legible even as a small thumbnail in an online store. 
Deliver the design in high resolution following the required aspect ratio.
Use a color palette and illustration or photography style that match the book's tone from description below: 
{description}
`
  },
  {
    id: 'fancy',
    name: 'Fancy & Artistic',
    description: 'Detailed, artistic cover with prominent text',
    template: `Generate a fancy ebook cover for an online library.

Book Title: {title}
Author: {author}

The cover should include the title "{title}" and author name "{author}" prominently displayed.
Create an artistic, professional-looking book cover suitable for an ebook library.
Use the following description to inform the style and mood:
{description}`
  },
  {
    id: 'simple',
    name: 'Simple & Clear',
    description: 'Clean design with clear, readable text',
    template: `Generate a simple and clean ebook cover.

Book Title: {title}
Author: {author}

Create a minimalist book cover with the title "{title}" and author "{author}" clearly displayed. Use a clean design with good contrast and readable text.
Description for context:
{description}`
  }
]

export function DescriptionCoverPromptDialog({ open, onClose, onConfirm, title, author }: DescriptionCoverPromptDialogProps) {
  const [selectedDescriptionTemplateId, setSelectedDescriptionTemplateId] = useState<string>('default')
  const [customDescriptionPrompt, setCustomDescriptionPrompt] = useState<string>('')
  
  const [selectedCoverTemplateId, setSelectedCoverTemplateId] = useState<string>('nano-banana-3-pro')
  const [customCoverPrompt, setCustomCoverPrompt] = useState<string>('')

  // Get the selected templates
  const selectedDescriptionTemplate = DESCRIPTION_PROMPT_TEMPLATES.find(t => t.id === selectedDescriptionTemplateId) || DESCRIPTION_PROMPT_TEMPLATES[0]
  const selectedCoverTemplate = COVER_PROMPT_TEMPLATES.find(t => t.id === selectedCoverTemplateId) || COVER_PROMPT_TEMPLATES[0]
  
  // Get the current prompts (either custom or from template)
  const currentDescriptionPrompt = customDescriptionPrompt || selectedDescriptionTemplate.template
  const currentCoverPrompt = customCoverPrompt || selectedCoverTemplate.template

  const handleDescriptionTemplateSelect = (templateId: string) => {
    setSelectedDescriptionTemplateId(templateId)
    setCustomDescriptionPrompt('')
  }

  const handleCoverTemplateSelect = (templateId: string) => {
    setSelectedCoverTemplateId(templateId)
    setCustomCoverPrompt('')
  }

  const handleConfirm = () => {
    const finalDescriptionPrompt = customDescriptionPrompt || selectedDescriptionTemplate.template
    const finalCoverPrompt = customCoverPrompt || selectedCoverTemplate.template
    // Pass cover model if template has it (and no custom prompt)
    const options = !customCoverPrompt && selectedCoverTemplate.model ? {
      coverModel: selectedCoverTemplate.model
    } : undefined
    onConfirm(finalDescriptionPrompt, finalCoverPrompt, options)
  }

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent 
        className="description-cover-prompt-dialog max-w-4xl max-h-[90vh] overflow-hidden flex flex-col [&>button]:hidden" 
        onInteractOutside={(e) => e.preventDefault()} 
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Generate Description + Cover
          </DialogTitle>
          <DialogDescription>
            Description is generated first, then used for cover. Use {'{title}'}, {'{author}'}, and {'{description}'} as placeholders.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 py-4">
          {/* Description Section */}
          <div className="description-section">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-semibold">Step 1: Description Prompt</h3>
            </div>
            
            {/* Description Template Tiles */}
            <div className="template-tiles grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
              {DESCRIPTION_PROMPT_TEMPLATES.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => handleDescriptionTemplateSelect(template.id)}
                  className={cn(
                    'template-tile p-3 rounded-lg border-2 text-left transition-all',
                    'hover:border-primary/50 hover:bg-accent',
                    selectedDescriptionTemplateId === template.id
                      ? 'border-primary bg-primary/5'
                      : 'border-border bg-background'
                  )}
                >
                  <div className="font-semibold text-sm">{template.name}</div>
                  <div className="text-xs text-muted-foreground">{template.description}</div>
                </button>
              ))}
            </div>

            {/* Description Prompt Editor */}
            <Textarea
              value={currentDescriptionPrompt}
              onChange={(e) => setCustomDescriptionPrompt(e.target.value)}
              placeholder="Enter your custom description prompt template..."
              className="min-h-[120px] font-mono text-sm"
            />
          </div>

          {/* Cover Section */}
          <div className="cover-section">
            <div className="flex items-center gap-2 mb-3">
              <BookImage className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-semibold">Step 2: Cover Prompt</h3>
              <span className="text-xs text-muted-foreground">(uses generated description)</span>
            </div>
            
            {/* Cover Template Tiles */}
            <div className="template-tiles grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
              {COVER_PROMPT_TEMPLATES.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => handleCoverTemplateSelect(template.id)}
                  className={cn(
                    'template-tile p-3 rounded-lg border-2 text-left transition-all',
                    'hover:border-primary/50 hover:bg-accent',
                    selectedCoverTemplateId === template.id
                      ? 'border-primary bg-primary/5'
                      : 'border-border bg-background'
                  )}
                >
                  <div className="font-semibold text-sm">{template.name}</div>
                  <div className="text-xs text-muted-foreground">{template.description}</div>
                </button>
              ))}
            </div>

            {/* Cover Prompt Editor */}
            <Textarea
              value={currentCoverPrompt}
              onChange={(e) => setCustomCoverPrompt(e.target.value)}
              placeholder="Enter your custom cover prompt template..."
              className="min-h-[120px] font-mono text-sm"
            />
            {selectedCoverTemplate.model && !customCoverPrompt && (
              <div className="text-xs text-primary mt-2">
                • Model: {selectedCoverTemplate.model}
              </div>
            )}
          </div>
        </div>

        <div className="text-xs text-muted-foreground border-t pt-3">
          Preview values: <code className="bg-muted px-1 rounded">{title}</code>, <code className="bg-muted px-1 rounded">{author}</code>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleConfirm}>
            <Sparkles className="mr-2 h-4 w-4" />
            Generate Both
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

